import { useRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';

import { numberFormat } from '@hotjar/insights-common/lib/number';
import { tokens } from '@hotjar/hotsauce';

import { useTranslatedClickEvent } from '../use-translated-click-event';

const TOP_MARGIN = 5;
const RIGHT_MARGIN = 5;
const BOX_HEIGHT = 36;

interface ComputeTopPositionProps {
  y1: number;
  y2: number;
  containerHeight: number;
  height: number;
  margin: number;
}

export function computeTopPosition({
  y1,
  y2,
  containerHeight,
  margin,
  height,
}: ComputeTopPositionProps): number {
  const showAbove = y2 + height > containerHeight;
  const top = showAbove ? y1 - height - margin : y2 + margin;
  return Math.max(top, 0);
}

interface ComputeLeftPositionProps {
  left: number;
  width: number;
  containerWidth: number;
  margin: number;
}

export function computeLeftPosition({
  left,
  width,
  containerWidth,
  margin,
}: ComputeLeftPositionProps): number {
  return Math.min(left, containerWidth - (width + margin));
}

const StyledDiv = styled.div<{
  top: number;
  left: number;
}>`
  ${tokens.transition('opacity')};
  ${tokens.text('subtitle2')}

  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  background: ${tokens.color.white};
  border-radius: ${tokens.radius.m};
  padding: ${tokens.spacer.x1};
  top: ${(props) => props.top}px;
  left: ${(props) => props.left}px;
  opacity: 0;
`;

export interface ClickStatsProps {
  y1: number;
  y2: number;
  left: number;
  containerHeight: number;
  containerWidth: number;
  clicksCount: number;
  clicksPercentage: number;
  isDesktop: boolean;
  className?: string;
}

export const ClickStats = ({
  y1,
  y2,
  left,
  containerHeight,
  containerWidth,
  isDesktop,
  clicksCount,
  clicksPercentage,
  className,
}: ClickStatsProps): JSX.Element | null => {
  const eventName = useTranslatedClickEvent({
    count: clicksCount,
    isDesktop,
  });

  const wrapper = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (wrapper?.current) {
      setWidth(wrapper.current.clientWidth);
    }
  }, [wrapper]);

  if (clicksCount === 0 || clicksPercentage === 0) {
    return null;
  }

  const top = computeTopPosition({
    y1,
    y2,
    containerHeight,
    margin: TOP_MARGIN,
    height: BOX_HEIGHT,
  });

  const x1 = computeLeftPosition({
    containerWidth,
    width,
    left,
    margin: RIGHT_MARGIN,
  });

  return (
    <StyledDiv ref={wrapper} className={className} left={x1} top={top}>
      {numberFormat(clicksCount)} {eventName} ({numberFormat(clicksPercentage)}%)
    </StyledDiv>
  );
};
